import React, { useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon, WrenchScrewdriverIcon } from '@heroicons/react/24/outline';

const EnhancedJobCardDetails = ({ jobCardsData }) => {
    const [expandedCards, setExpandedCards] = useState({});
    const [expandedParts, setExpandedParts] = useState({});

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-KE', {
            style: 'currency',
            currency: 'KES',
            minimumFractionDigits: 2
        }).format(value);
    };

    const toggleCard = (jobCardId) => {
        setExpandedCards(prev => ({
            ...prev,
            [jobCardId]: !prev[jobCardId]
        }));
    };

    const toggleParts = (jobCardId) => {
        setExpandedParts(prev => ({
            ...prev,
            [jobCardId]: !prev[jobCardId]
        }));
    };

    if (!jobCardsData || Object.keys(jobCardsData).length === 0) {
        return (
            <div className="text-center py-12">
                <WrenchScrewdriverIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500 text-lg">No job cards found for this period</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Summary Stats */}
            {(() => {
                let totalJobCards = 0;
                let totalCost = 0;
                let totalParts = 0;
                let totalLabor = 0;

                Object.values(jobCardsData).forEach(plateGroup => {
                    plateGroup.job_cards?.forEach(jc => {
                        totalJobCards++;
                        totalCost += parseFloat(jc.total_cost || 0);
                        totalParts += parseFloat(jc.parts_cost || 0);
                        totalLabor += parseFloat(jc.labor_cost || 0);
                    });
                });

                return (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4">
                            <div className="text-sm font-semibold text-blue-800">Total Job Cards</div>
                            <div className="text-3xl font-bold text-blue-900 text-right mt-2">
                                {totalJobCards}
                            </div>
                        </div>
                        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4">
                            <div className="text-sm font-semibold text-green-800">Total Cost</div>
                            <div className="text-2xl font-bold text-green-900 text-right mt-2">
                                {formatCurrency(totalCost)}
                            </div>
                        </div>
                        <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4">
                            <div className="text-sm font-semibold text-purple-800">Parts Cost</div>
                            <div className="text-2xl font-bold text-purple-900 text-right mt-2">
                                {formatCurrency(totalParts)}
                            </div>
                        </div>
                        <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-4">
                            <div className="text-sm font-semibold text-orange-800">Labor Cost</div>
                            <div className="text-2xl font-bold text-orange-900 text-right mt-2">
                                {formatCurrency(totalLabor)}
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* Job Cards Grouped by Vehicle */}
            {Object.entries(jobCardsData).map(([plateNo, plateGroup]) => (
                <div key={plateNo} className="bg-white rounded-lg shadow-md overflow-hidden">
                    <div className="bg-gray-100 px-6 py-4 border-b border-gray-200">
                        <div className="flex items-center justify-between">
                            <h3 className="text-xl font-bold text-[#2a2e30]">
                                {plateNo}
                            </h3>
                            <span className="text-sm text-gray-600">
                                {plateGroup.job_cards?.length || 0} {plateGroup.job_cards?.length === 1 ? 'Job Card' : 'Job Cards'}
                            </span>
                        </div>
                    </div>

                    <div className="p-6 space-y-4">
                        {plateGroup.job_cards?.map((jobCard) => (
                            <div key={jobCard.id} className="border border-gray-200 rounded-lg overflow-hidden">
                                {/* Job Card Header */}
                                <div
                                    className="bg-gray-50 px-4 py-3 cursor-pointer hover:bg-gray-100 transition-colors"
                                    onClick={() => toggleCard(jobCard.id)}
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-3">
                                                {expandedCards[jobCard.id] ? (
                                                    <ChevronDownIcon className="w-5 h-5 text-gray-600" />
                                                ) : (
                                                    <ChevronUpIcon className="w-5 h-5 text-gray-600" />
                                                )}
                                                <div>
                                                    <div className="font-bold text-[#2a2e30]">
                                                        JC# {jobCard.job_card_no}
                                                    </div>
                                                    <div className="text-sm text-gray-600">
                                                        {new Date(jobCard.opened_date).toLocaleDateString('en-US', {
                                                            month: 'short',
                                                            day: 'numeric',
                                                            year: 'numeric'
                                                        })}
                                                        {jobCard.service_center_name && ` • ${jobCard.service_center_name}`}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <div className="text-xl font-bold text-[#2a2e30]">
                                                {formatCurrency(jobCard.total_cost || 0)}
                                            </div>
                                            <div className="flex items-center gap-2 justify-end">
                                                <span className={`px-2 py-1 rounded-full text-xs font-semibold ${jobCard.status === 'COMPLETED_INVOICED'
                                                        ? 'bg-green-100 text-green-800'
                                                        : 'bg-blue-100 text-blue-800'
                                                    }`}>
                                                    {jobCard.status}
                                                </span>
                                                <span className={`px-2 py-1 rounded-full text-xs font-semibold ${jobCard.paid_by === 'OFFICE'
                                                        ? 'bg-red-100 text-red-800'
                                                        : jobCard.paid_by === 'OWNER'
                                                            ? 'bg-purple-100 text-purple-800'
                                                            : 'bg-gray-100 text-gray-800'
                                                    }`}>
                                                    {jobCard.paid_by}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Expanded Details */}
                                {expandedCards[jobCard.id] && (
                                    <div className="p-4 space-y-4">
                                        {/* Diagnosis and Work Performed */}
                                        {(jobCard.diagnosis || jobCard.work_performed) && (
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                {jobCard.diagnosis && (
                                                    <div>
                                                        <div className="text-xs font-semibold text-gray-600 mb-1">Diagnosis:</div>
                                                        <div className="text-sm bg-gray-50 p-2 rounded">
                                                            {jobCard.diagnosis}
                                                        </div>
                                                    </div>
                                                )}
                                                {jobCard.work_performed && (
                                                    <div>
                                                        <div className="text-xs font-semibold text-gray-600 mb-1">Work Performed:</div>
                                                        <div className="text-sm bg-gray-50 p-2 rounded">
                                                            {jobCard.work_performed}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* Cost Breakdown */}
                                        <div className="grid grid-cols-3 gap-3">
                                            <div className="bg-blue-50 rounded p-3">
                                                <div className="text-xs text-blue-800 font-semibold">Parts</div>
                                                <div className="text-lg font-bold text-blue-900 text-right">
                                                    {formatCurrency(jobCard.parts_cost || 0)}
                                                </div>
                                            </div>
                                            <div className="bg-green-50 rounded p-3">
                                                <div className="text-xs text-green-800 font-semibold">Labor</div>
                                                <div className="text-lg font-bold text-green-900 text-right">
                                                    {formatCurrency(jobCard.labor_cost || 0)}
                                                </div>
                                            </div>
                                            <div className="bg-purple-50 rounded p-3">
                                                <div className="text-xs text-purple-800 font-semibold">Charges</div>
                                                <div className="text-lg font-bold text-purple-900 text-right">
                                                    {formatCurrency(jobCard.charges_cost || 0)}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Parts Inventory Details */}
                                        {jobCard.parts && jobCard.parts.length > 0 && (
                                            <div className="border border-gray-200 rounded">
                                                <div
                                                    className="bg-gray-50 px-3 py-2 cursor-pointer hover:bg-gray-100"
                                                    onClick={() => toggleParts(jobCard.id)}
                                                >
                                                    <div className="flex items-center justify-between">
                                                        <span className="text-sm font-semibold text-[#2a2e30]">
                                                            Parts Used ({jobCard.parts.length})
                                                        </span>
                                                        {expandedParts[jobCard.id] ? (
                                                            <ChevronDownIcon className="w-4 h-4" />
                                                        ) : (
                                                            <ChevronUpIcon className="w-4 h-4" />
                                                        )}
                                                    </div>
                                                </div>
                                                {expandedParts[jobCard.id] && (
                                                    <div className="p-3">
                                                        <table className="min-w-full text-sm">
                                                            <thead className="bg-gray-50">
                                                                <tr>
                                                                    <th className="px-2 py-1 text-left text-xs font-semibold text-gray-700">Part Name</th>
                                                                    <th className="px-2 py-1 text-right text-xs font-semibold text-gray-700">Qty</th>
                                                                    <th className="px-2 py-1 text-right text-xs font-semibold text-gray-700">Unit Cost</th>
                                                                    <th className="px-2 py-1 text-right text-xs font-semibold text-gray-700">Total</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody className="divide-y divide-gray-200">
                                                                {jobCard.parts.map((part, idx) => (
                                                                    <tr key={idx} className="hover:bg-gray-50">
                                                                        <td className="px-2 py-2">{part.part_name}</td>
                                                                        <td className="px-2 py-2 text-right">{part.quantity}</td>
                                                                        <td className="px-2 py-2 text-right font-semibold">
                                                                            {formatCurrency(part.unit_cost || 0)}
                                                                        </td>
                                                                        <td className="px-2 py-2 text-right font-bold">
                                                                            {formatCurrency((part.quantity || 0) * (part.unit_cost || 0))}
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* Labor/Charges Details */}
                                        {jobCard.charges && jobCard.charges.length > 0 && (
                                            <div className="border border-gray-200 rounded p-3">
                                                <div className="text-sm font-semibold text-[#2a2e30] mb-2">
                                                    Labor & Charges
                                                </div>
                                                <div className="space-y-1">
                                                    {jobCard.charges.map((charge, idx) => (
                                                        <div key={idx} className="flex justify-between text-sm bg-gray-50 p-2 rounded">
                                                            <span>{charge.service_type || charge.description}</span>
                                                            <span className="font-semibold">
                                                                {formatCurrency(charge.cost || 0)}
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
};

export default EnhancedJobCardDetails;
